import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { HeadingDirective, type HeadingLevel } from '@presentation/directives';

/**
 * Heading Component
 *
 * Renders a semantic heading element (`<h1>`–`<h4>`) based on the `level` input,
 * so the DOM tag matches the intended document hierarchy (accessibility).
 * Visual styling is applied via {@link HeadingDirective}, keeping the look
 * consistent regardless of the rendered tag.
 *
 * Use `visualLevel` when the semantic tag must differ from the visual style
 * (e.g. an `<h2>` in the DOM that should look like an `<h1>`). When omitted,
 * the visual style matches `level`.
 */
@Component({
  selector: 'cm-heading',
  standalone: true,
  imports: [HeadingDirective, NgTemplateOutlet],
  templateUrl: './heading.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeadingComponent {
  /** Semantic heading level, controls the rendered DOM tag. Defaults to `h1`. */
  public readonly level = input<HeadingLevel>('h1');
  /** Optional visual style override. Falls back to `level` when not provided. */
  public readonly visualLevel = input<HeadingLevel>();
}
